import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import ComplianceStatePanel from '../components/cdp/ComplianceStatePanel';
import CDPRevisionHistory from '../components/cdp/CDPRevisionHistory';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, Clock, AlertCircle, ChevronDown, ChevronRight,
  ExternalLink, BookOpen, Briefcase, GraduationCap, FileText, User,
  Award, Wrench, Target, BarChart3, Zap, Plus, ArrowRight
} from 'lucide-react';import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_ICONS = {
  'About You': User,
  'Goals': Target,
  'Education': GraduationCap,
  'Current & Future Courses': BookOpen,
  'Work-Based Learning': Briefcase,
  'Achievements': Award,
  'Accommodations': FileText,
  'Resume': FileText,
  'Notes': BookOpen,
};

const STATUS_CONFIG = {
  complete: { color: 'text-cyan-300', bg: 'bg-cyan-500/10 border-cyan-400/30', icon: CheckCircle2, label: 'Complete', glow: '0 0 26px -10px #22d3ee' },
  in_progress: { color: 'text-violet-300', bg: 'bg-violet-500/10 border-violet-400/30', icon: Clock, label: 'In Progress', glow: '0 0 26px -10px #a78bfa' },
  incomplete: { color: 'text-sky-300', bg: 'bg-sky-500/10 border-sky-400/30', icon: AlertCircle, label: 'Incomplete', glow: '0 0 22px -12px #38bdf8' },
  not_started: { color: 'text-slate-300', bg: 'bg-slate-800/40 border-slate-600/30', icon: Circle, label: 'Not Started', glow: 'none' },
};

function SectionCard({ section, index, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = SECTION_ICONS[section.label] || FileText;
  const status = STATUS_CONFIG[section.status] || STATUS_CONFIG.not_started;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-xl border ${status.bg} overflow-hidden`}
      style={{ boxShadow: status.glow }}
      data-testid={`cdp-section-${index}`}
    >
      <button
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-9 h-9 rounded-lg bg-slate-800/60 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-slate-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-300 font-medium">{section.nc_ref}</span>
            <span className="text-white font-medium text-sm">{section.label}</span>
            {section.coming_soon && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600">
                Coming Soon
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden" style={{ maxWidth: 120 }}>
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  section.completion >= 80 ? 'bg-gradient-to-r from-cyan-400 to-cyan-300' :
                  section.completion >= 40 ? 'bg-gradient-to-r from-violet-400 to-cyan-400' : 'bg-sky-400/60'
                }`}
                style={{ width: `${section.completion}%` }}
              />
            </div>
            <span className="text-xs text-slate-300">{section.completion}%</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
          <StatusIcon className={`w-4 h-4 ${status.color}`} />
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 px-4 pb-4 pt-3"
          >
            {section.action && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 mb-3">
                <p className="text-sm text-slate-300">{section.action}</p>
                {section.action_path && (
                  <Link
                    to={section.action_path}
                    className="flex-shrink-0 flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 font-medium"
                  >
                    Go <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            )}

            {/* Section-specific data display */}
            {section.label === 'About You' && section.data?.riasec_code && (
              <div className="text-sm text-slate-300">
                <span className="text-slate-300">RIASEC Code: </span>
                <span className="font-mono text-cyan-300">{section.data.riasec_code}</span>
                {section.data.personality_type && (
                  <span className="ml-3 text-slate-300">"{section.data.personality_type}"</span>
                )}
              </div>
            )}

            {section.label === 'Goals' && (
              <div className="text-sm text-slate-300 space-y-1">
                {section.data?.career_goal && <p><span className="text-slate-300">Career Goal: </span>{section.data.career_goal}</p>}
                {section.data?.active_goals_count > 0 && <p><span className="text-slate-300">Active Milestones: </span>{section.data.active_goals_count}</p>}
              </div>
            )}

            {section.label === 'Work-Based Learning' && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-slate-300">Total Hours:</span>
                  <span className={`font-medium ${section.data?.total_hours >= 40 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {section.data?.total_hours || 0} / 40 hrs
                  </span>
                </div>
                {section.data?.experiences?.map((exp, i) => (
                  <div key={i} className="text-xs text-slate-300 flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-slate-700 capitalize">{exp.type?.replace('_', ' ')}</span>
                    <span>{exp.employer}</span>
                    <span className="text-slate-300">• {exp.hours}h</span>
                    {exp.verified && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                  </div>
                ))}
              </div>
            )}

            {section.label === 'Achievements' && (
              <div className="text-sm text-slate-300 space-y-1">
                <p><span className="text-slate-300">Uploaded Documents: </span>{section.data?.uploaded_documents}</p>
                <p><span className="text-slate-300">Portfolio Items: </span>{section.data?.portfolio_items}</p>
              </div>
            )}

            {section.label === 'Notes' && section.data?.recent_notes?.length > 0 && (
              <div className="space-y-2">
                {section.data.recent_notes.map((note, i) => (
                  <div key={i} className="text-xs text-slate-300 p-2 rounded bg-slate-800/50">
                    <p className="text-slate-300 mb-0.5">{note.counselor_name} · {note.created_at?.slice(0,10)}</p>
                    <p>{note.note}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function EndorsementMeter({ progress }) {
  const checks = progress?.checks || {};
  const labels = {
    assessment_complete: 'Assessment complete',
    career_goal_set: 'Career goal set',
    cte_pathway_mapped: 'NC cluster auto-mapped',
    credential_earned: 'Industry credential earned',
    wbl_hours_40plus: 'WBL hours (40+)',
    resume_uploaded: 'Resume uploaded',
  };

  return (
    <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/5 p-5"
      style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.55)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold text-sm">NC Career Endorsement Progress</h3>
          <p className="text-xs text-slate-300 mt-0.5">Tracks toward the NC Career Endorsement diploma</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-cyan-300">{progress?.percentage || 0}%</div>
        </div>
      </div>
      <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-cyan-400 via-sky-400 to-violet-500 rounded-full transition-all duration-700"
          style={{ width: `${progress?.percentage || 0}%`, boxShadow: '0 0 18px -6px rgba(34,211,238,0.7)' }}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(labels).map(([key, label]) => (
          <div key={key} className="flex items-center gap-2 text-xs">
            {checks[key] ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
            )}
            <span className={checks[key] ? 'text-slate-300' : 'text-slate-300'}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InitialDirectionBlock({ initial, timeline }) {
  const i = initial?.initial || {};
  const reviewDue = initial?.status === 'review_due';
  const createdYear = i.created_at ? new Date(i.created_at).getFullYear() : null;

  const ord = (n) => {
    if (n == null) return '';
    const s = ['th', 'st', 'nd', 'rd']; const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      data-testid="cdp-initial-direction"
      className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/60 p-6"
      style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.5)' }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />

      <div className="relative">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
            text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
            Initial Direction · {ord(i.created_grade || 8)} Grade
          </span>
          {reviewDue ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5
              text-[10px] font-semibold uppercase tracking-wider text-amber-200">
              Review due · {ord(initial.next_required_review_grade)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5
              text-[10px] font-semibold uppercase tracking-wider text-cyan-200">
              Current
            </span>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-300 leading-relaxed">
          This is the direction you entered high school with. FuturePath will help you explore, test, and refine it as you learn more about yourself.
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/[0.05] p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">Career interest</p>
            <p className="mt-1 text-lg font-semibold text-white">{i.career_interest || '-'}</p>
          </div>
          <div className="rounded-xl border border-violet-300/20 bg-violet-400/[0.05] p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">NC Career Cluster</p>
            <p className="mt-1 text-sm font-semibold text-white">{i.cluster || '-'}</p>
          </div>
          <div className="rounded-xl border border-sky-300/20 bg-sky-400/[0.05] p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-300">Postsecondary</p>
            <p className="mt-1 text-lg font-semibold text-white">{i.postsecondary_direction || '-'}</p>
          </div>
        </div>

        {(i.top_interests?.length > 0 || i.strengths?.length > 0) && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {i.top_interests?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-1.5">Top interests</p>
                <div className="flex flex-wrap gap-1.5">
                  {i.top_interests.map((t) => (
                    <span key={t} className="rounded-full border border-cyan-300/25 bg-cyan-400/[0.06] px-2 py-0.5 text-xs text-cyan-100">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {i.strengths?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-1.5">Strengths</p>
                <div className="flex flex-wrap gap-1.5">
                  {i.strengths.map((s) => (
                    <span key={s} className="rounded-full border border-violet-300/25 bg-violet-400/[0.06] px-2 py-0.5 text-xs text-violet-100">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Evolution timeline */}
        {timeline?.length > 0 && (
          <div className="mt-6 pt-5 border-t border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-3">CDP History</p>
            <div className="space-y-2">
              {timeline.map((e, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="mt-0.5 h-2 w-2 rounded-full flex-shrink-0"
                    style={{ background: e.type === 'initial' ? '#22d3ee' : '#a78bfa' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">
                      {ord(e.grade)} Grade, {e.career_interest || 'Undecided'}
                      {e.cluster && <span className="text-slate-300"> · {e.cluster}</span>}
                    </p>
                    <p className="text-[11px] text-slate-300">{e.note}{createdYear && e.type === 'initial' ? ` · ${createdYear}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}


export default function NCCareerPlanPage() {
  const { getAuthHeader } = useAuth();
  const [cdp, setCdp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNCToggle, setShowNCToggle] = useState(false);
  const [initial, setInitial] = useState(null);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    axios.get(`${API}/nc/cdp`, getAuthHeader())
      .then(r => setCdp(r.data))
      .catch(() => toast.error('Could not load your Career Development Plan'))
      .finally(() => setLoading(false));
    axios.get(`${API}/nc/cdp/initial-direction`, getAuthHeader())
      .then(r => setInitial(r.data)).catch(() => {});
    axios.get(`${API}/nc/cdp/timeline`, getAuthHeader())
      .then(r => setTimeline(r.data?.entries || [])).catch(() => {});
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const sections = cdp?.sections || [];
  const completion = cdp?.overall_completion || 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-12" data-testid="nc-cdp-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">North Carolina</span>
              </div>
              <h1 className="text-3xl font-bold text-white">Career Development Plan</h1>
              <p className="text-slate-300 mt-1 text-sm">Required by NC Gen. Stat. § 115C-158.10, 9 sections, updated annually</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-white">{completion}%</div>
              <div className="text-sm text-slate-300">Complete</div>
            </div>
          </div>

          {/* Overall progress bar */}
          <div className="mt-4 h-2 w-full bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${completion}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-violet-500"
              style={{ boxShadow: '0 0 20px -6px rgba(34,211,238,0.6)' }}
            />
          </div>
        </motion.div>

        {/* Initial Direction, the 8th-grade CDP the student entered high school with */}
        {initial?.has_initial_direction && (
          <InitialDirectionBlock initial={initial} timeline={timeline} />
        )}

        {/* NC state compliance, proves the platform satisfies § 115C-158.10 */}
        <ComplianceStatePanel />

        {/* Revision history, every change is logged for counselor + parent visibility */}
        <CDPRevisionHistory />

        {/* Career → NC Cluster, AI-derived, opt-out available */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          {cdp?.cte_pathway ? (
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs text-slate-300">Your career goal</span>
                    {cdp.career_goal && (
                      <span className="text-xs font-semibold text-white bg-slate-700/60 px-2 py-0.5 rounded-full">
                        {cdp.career_goal}
                      </span>
                    )}
                    <span className="text-slate-300 text-xs">→ maps to NC cluster</span>
                  </div>
                  <p className="text-white font-semibold text-base">{cdp.cte_pathway.name}</p>
                  {cdp.cte_pathway.reasoning && (
                    <p className="text-slate-300 text-xs mt-1 italic">"{cdp.cte_pathway.reasoning}"</p>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {cdp.cte_pathway.pathways?.slice(0, 3).map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/60 border border-slate-600/50 text-slate-300">{p}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {cdp.cluster_source === 'counselor_override' && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 font-medium">
                      Counselor set
                    </span>
                  )}
                  <button
                    onClick={() => setShowNCToggle(v => !v)}
                    className="text-[10px] text-slate-300 hover:text-slate-300 underline"
                    data-testid="nc-cluster-toggle"
                  >
                    {showNCToggle ? 'Hide NC info' : 'What is this?'}
                  </button>
                </div>
              </div>
              {showNCToggle && (
                <div className="mt-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/40 text-xs text-slate-300 space-y-1.5">
                  <p>
                    <span className="text-slate-300 font-medium">Why does this appear?</span> North Carolina requires schools to track
                    which of 16 career clusters each student is working toward. FuturePath satisfies this automatically
                    from your career goal, you never have to pick a cluster.
                  </p>
                  <p>
                    <span className="text-slate-300 font-medium">Does this limit me?</span> No. Your FuturePath is completely open.
                    This is just a compliance label NC needs, it does not restrict what you explore or pursue.
                  </p>
                  <p className="text-slate-300">
                    If NC's cluster categories don't fit your career path, that's NC's limitation, not yours.
                    FuturePath flags it so your counselor can note it in your file.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-600/60 bg-slate-800/20 p-4">
              <p className="text-sm text-slate-300 font-medium mb-0.5">NC Career Cluster, not yet mapped</p>
              <p className="text-xs text-slate-300 mb-3">
                Set your career goal anywhere in FuturePath and your NC cluster will appear here automatically.
                No form needed.
              </p>
              <Link
                to="/app/goals"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 text-xs font-medium hover:bg-cyan-600/30 transition-colors"
              >
                <Target className="w-3 h-3" /> Set my career goal
              </Link>
            </div>
          )}
        </motion.div>

        {/* 9 Sections */}
        <div className="space-y-3">
          {sections.map((section, i) => (
            <SectionCard key={section.nc_ref} section={section} index={i} />
          ))}
        </div>

        {/* Career Endorsement meter */}
        {cdp?.endorsement_progress && (
          <EndorsementMeter progress={cdp.endorsement_progress} />
        )}

        {/* NC Links */}
        <div className="text-xs text-slate-300 flex items-center gap-3 flex-wrap">
          <a href="https://nccareers.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-300 transition-colors">
            NCcareers.org <ExternalLink className="w-3 h-3" />
          </a>
          <a href="https://www.dpi.nc.gov/career-development-plan-cdpguidance" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-300 transition-colors">
            NC DPI CDP Guidance <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
